import { useState, useEffect } from "react";
import Header from "../../components/common/layout/Header";
import Footer from "../../components/common/layout/Footer";
import ProductPlansDisplay from "@/components/features/shared/ProductPlansDisplay/ProductPlansDisplay";
import OtherServices from "@/components/features/emailhosting/OtherServices/OtherServices";
import WebBuilderFeature from "@/components/features/emailhosting/WebBuilderFeature/WebBuilderFeature";
import FAQSection from "../../components/common/sections/FAQ";
import Breadcrumbs from "@/components/common/sections/Breadcrumbs";
import BreadcrumbsImage from "@/assets/images/breadcrumbs/webhosting.webp";
import { getLinuxHostingPlans } from "@/services/dreamscapeApi";
import { CurrencyCode } from "@/utils/currencyConverter";
import SEO from "@/components/common/seo/seo";

export default function WebHosting() {
    const [plans, setPlans] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedCurrency, setSelectedCurrency] = useState<CurrencyCode>('USD');
    const [isDummyData, setIsDummyData] = useState(false);

    useEffect(() => {
        const loadPlans = async () => {
            setLoading(true);
            setError(null);
            setIsDummyData(false);

            try {
                const result = await getLinuxHostingPlans({ limit: 50 });

                if (result.success && result.plans && result.plans.length > 0) {
                    setPlans(result.plans);
                    setIsDummyData(false);
                } else {
                    console.warn('No plans returned from API, using dummy data:', result.message);
                    setIsDummyData(true);
                    loadDummyPlans();
                }
            } catch (err) {
                console.error('Failed to fetch Linux hosting plans:', err);
                setIsDummyData(true);
                loadDummyPlans();
            } finally {
                setLoading(false);
            }
        };

        loadPlans();
    }, []);

    const loadDummyPlans = () => {
        const dummyPlans = [
            {
                id: 'dummy-1',
                name: 'Starter Hosting',
                periods: [{ period: 1, price: { register: 2.99, renew: 5.99 } }],
                features: ['1 Website', '10GB SSD Storage', 'Unlimited Bandwidth', 'Free SSL']
            },
            {
                id: 'dummy-2',
                name: 'Business Hosting',
                periods: [{ period: 1, price: { register: 5.99, renew: 10.99 } }],
                features: ['Unlimited Websites', '100GB SSD Storage', 'Unlimited Bandwidth', 'Free SSL', 'Daily Backups']
            },
            {
                id: 'dummy-3',
                name: 'Professional Hosting',
                periods: [{ period: 1, price: { register: 9.99, renew: 15.99 } }],
                features: ['Unlimited Websites', 'Unlimited SSD Storage', 'Priority Support', 'Dedicated IP', 'Advanced Security']
            }
        ];
        setPlans(dummyPlans);
    };

    const handleSelectPlan = (plan: any, period: number, price: number) => {
        console.log('Selected Web hosting plan:', plan, 'Period:', period, 'Price:', price);
        // TODO: Implement cart/checkout flow
    };

    return (
        <div className="bg-[#0b0b1a]">
            <SEO
                title="Best Linux Web Hosting - SAN micron"
                description="Fast, secure, and reliable Linux web hosting for your website. Features include SSD storage, unlimited bandwidth, and 24/7 support."
            />
            <Header />

            <Breadcrumbs
                title="Web Hosting"
                backgroundImage={BreadcrumbsImage}
                breadcrumbText="Web Hosting"
            />

            <section id="plans" className="py-24 container mx-auto">
                <div className="text-center mb-16 px-4">
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Choose Your Plan</h2>
                    <p className="text-textColor/70 text-lg max-w-2xl mx-auto">
                        Scale your website with our robust hosting solutions. Start small and grow as you need.
                    </p>
                    {isDummyData && (
                        <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg inline-block">
                            <p className="text-yellow-500 text-sm">
                                Note: Currently showing demo pricing. Please contact us for live rates.
                            </p>
                        </div>
                    )}
                </div>

                <div className="hosting-plans-grid">
                    <ProductPlansDisplay
                        plans={plans}
                        loading={loading}
                        error={error}
                        currency={selectedCurrency}
                        onCurrencyChange={setSelectedCurrency}
                        onSelectPlan={handleSelectPlan}
                        buttonText="BUY NOW"
                    />
                </div>
            </section>

            <OtherServices />
            <WebBuilderFeature />

            <FAQSection faqs={[]} />

            <Footer />
        </div>
    );
}
